import axiosInstance from '../../core/axiosConfig.ts';
import * as Sentry from '@sentry/react';

export type SupportContact = {
    whatsappNumber: string | null;
};

/** The Vello Credit rates. Amounts in EUR, rate in percent. */
export type CreditSettings = {
    enabled: boolean;
    earnRatePercent: number;
    tripBonusAmount: number;
    tripBonusHours: number;
    referralAmount: number;
    referralUnlockSpend: number;
    referralMinFirstOrder: number;
    expiryMonths: number;
    /** True limits redemption to the food, so delivery and service are always paid in money. */
    foodOnly: boolean;
    /** Hours the invited customer's welcome credit lasts. 0 = standard expiry. */
    referralExpiryHours: number;
    /** How many customers one invite code may bring in. 0 = unlimited. */
    referralMaxUses: number;
};

export type RestaurantCreditPayout = {
    locationId: number;
    locationName: string;
    orders: number;
    amount: number;
};

export type CreditSummary = {
    outstandingLiability: number;
    issuedThisMonth: number;
    redeemedThisMonth: number;
    expiredThisMonth: number;
    commissionThisMonth: number;
    issuedToCommissionPercent: number | null;
    /** To be transferred to restaurants for this month: credit that covered part of their payouts. */
    owedToRestaurantsThisMonth: number;
    restaurantPayoutsThisMonth: RestaurantCreditPayout[];
};

export default class SettingsService {
    async getSupportContact(): Promise<SupportContact> {
        const response = await axiosInstance.get('/settings/support');
        return response.data;
    }

    /**
     * The backend normalises to E.164 and rejects a number without a country
     * code, so the value that comes back is the one customers will actually be
     * sent to - not necessarily the string that was typed. Callers should render
     * the response rather than what they submitted.
     */
    async updateSupportContact(whatsappNumber: string): Promise<SupportContact> {
        try {
            const response = await axiosInstance.patch('/settings/support', {
                whatsappNumber
            });
            return response.data;
        } catch (e: any) {
            Sentry.captureException(e);
            throw e;
        }
    }

    async getCreditSettings(): Promise<CreditSettings> {
        const response = await axiosInstance.get('/settings/credit');
        return response.data;
    }

    async updateCreditSettings(settings: CreditSettings): Promise<CreditSettings> {
        try {
            const response = await axiosInstance.patch('/settings/credit', settings);
            return response.data;
        } catch (e: any) {
            Sentry.captureException(e);
            throw e;
        }
    }

    async getCreditSummary(): Promise<CreditSummary> {
        const response = await axiosInstance.get('/settings/credit/summary');
        return response.data;
    }
}
